import React, { useState, useEffect } from 'react';
import microbiologyService from '../../../services/microbiologyService';
import CommonPagination from '../../../components/common/CommonPagination';
import usePagination from '../../../hooks/usePagination';
import './MicrobiologyMasters.css';

export default function OrganismMasterPage() {
  const [organisms, setOrganisms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedGram, setSelectedGram] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);

  // Centralized Standardized Pagination
  const pagination = usePagination({
    items: organisms,
    initialPage: 1,
    initialPageSize: 15,
    resetDeps: [search, selectedGroup, selectedGram]
  });

  // Extended form state
  const [formData, setFormData] = useState({
    organismCode: '',
    organismName: '',
    shortName: '',
    displayName: '',
    synonyms: '',
    organismGroupID: '',
    kingdom: 'Bacteria',
    phylum: 'Pseudomonadota',
    taxClass: 'Gammaproteobacteria',
    order: 'Enterobacterales',
    organismFamily: 'Enterobacteriaceae',
    genus: '',
    species: '',
    gramType: 'Gram Negative',
    morphology: 'Bacilli',
    customMorphology: '',
    aerobicType: 'Facultative Anaerobe',
    cellArrangement: 'Single',
    motility: 'Motile',
    sporeFormation: 'Non-spore-forming',
    capsule: 'Variable',
    acidFast: 'Negative',
    clinicalCategory: 'Primary Pathogen',
    clinicalNotes: '',
    status: 'Draft',
    displayOrder: 0,
    remarks: '',
    referenceSource: 'CLSI M100 / NCBI Taxonomy',
    referenceVersion: '2026.1',
    lastReviewedDate: new Date().toISOString().split('T')[0]
  });

  const [synonymInput, setSynonymInput] = useState('');
  const [synonymTags, setSynonymTags] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const loadData = async () => {
    try {
      setLoading(true);
      const [orgRes, grpRes] = await Promise.all([
        microbiologyService.getOrganisms({
          search: search || undefined,
          groupId: selectedGroup || undefined,
          gramType: selectedGram || undefined
        }),
        microbiologyService.getOrganismGroups()
      ]);
      setOrganisms(orgRes?.data || []);
      setGroups(grpRes?.data || []);
    } catch (err) {
      console.error('Error loading organisms:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [search, selectedGroup, selectedGram]);

  const handleOpenAdd = () => {
    setEditingOrg(null);
    setValidationErrors({});
    setSynonymTags([]);
    setSynonymInput('');
    setFormData({
      organismCode: '',
      organismName: '',
      shortName: '',
      displayName: '',
      synonyms: '',
      organismGroupID: groups[0]?.organismGroupID || 1,
      kingdom: 'Bacteria',
      phylum: 'Pseudomonadota',
      taxClass: 'Gammaproteobacteria',
      order: 'Enterobacterales',
      organismFamily: 'Enterobacteriaceae',
      genus: '',
      species: '',
      gramType: 'Gram Negative',
      morphology: 'Bacilli',
      customMorphology: '',
      aerobicType: 'Facultative Anaerobe',
      cellArrangement: 'Single',
      motility: 'Motile',
      sporeFormation: 'Non-spore-forming',
      capsule: 'Variable',
      acidFast: 'Negative',
      clinicalCategory: 'Primary Pathogen',
      clinicalNotes: '',
      status: 'Draft',
      displayOrder: organisms.length + 1,
      remarks: '',
      referenceSource: 'CLSI M100 / NCBI Taxonomy',
      referenceVersion: '2026.1',
      lastReviewedDate: new Date().toISOString().split('T')[0]
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (org) => {
    setEditingOrg(org);
    setValidationErrors({});
    const existingTags = org.synonyms
      ? org.synonyms.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
    setSynonymTags(existingTags);
    setSynonymInput('');

    // Extract genus / species if binomial
    const nameParts = (org.organismName || '').trim().split(' ');
    const autoGenus = nameParts[0] || '';
    const autoSpecies = nameParts.slice(1).join(' ') || '';

    // Check if morphology is standard or custom
    const standardMorphologies = [
      'Cocci', 'Bacilli', 'Coccobacilli', 'Curved Rods', 'Spiral',
      'Filamentous', 'Yeast', 'Hyphal'
    ];
    const isStandardMorph = standardMorphologies.includes(org.morphology);

    setFormData({
      organismID: org.organismID,
      organismCode: org.organismCode || '',
      organismName: org.organismName || '',
      shortName: org.shortName || '',
      displayName: org.shortName ? `${org.organismName} (${org.shortName})` : org.organismName,
      synonyms: org.synonyms || '',
      organismGroupID: org.organismGroupID || groups[0]?.organismGroupID || 1,
      kingdom: org.gramType === 'Yeast' || org.gramType === 'Mold' ? 'Fungi' : 'Bacteria',
      phylum: 'Pseudomonadota',
      taxClass: 'Gammaproteobacteria',
      order: 'Enterobacterales',
      organismFamily: org.organismFamily || '',
      genus: autoGenus,
      species: autoSpecies,
      gramType: org.gramType || 'Gram Negative',
      morphology: isStandardMorph ? org.morphology : (org.morphology ? 'Other' : 'Bacilli'),
      customMorphology: isStandardMorph ? '' : (org.morphology || ''),
      aerobicType: org.aerobicType || 'Facultative Anaerobe',
      cellArrangement: 'Single',
      motility: 'Unknown',
      sporeFormation: 'Non-spore-forming',
      capsule: 'Unknown',
      acidFast: org.gramType === 'Acid-Fast' ? 'Positive' : 'Negative',
      clinicalCategory: org.clinicalCategory || 'Primary Pathogen',
      clinicalNotes: '',
      status: org.isActive ? 'Active' : 'Inactive',
      displayOrder: org.displayOrder || 0,
      remarks: org.remarks || '',
      referenceSource: 'CLSI M100 / NCBI Taxonomy',
      referenceVersion: '2026.1',
      lastReviewedDate: new Date().toISOString().split('T')[0]
    });
    setIsModalOpen(true);
  };

  // Synchronize scientific name with genus/species/display preview
  const handleScientificNameChange = (val) => {
    const parts = val.trim().split(' ');
    const autoGenus = parts[0] || '';
    const autoSpecies = parts.slice(1).join(' ') || '';
    
    // Auto-generate short name if not manually overridden
    let autoShort = formData.shortName;
    if (autoGenus && autoSpecies && (!formData.shortName || formData.shortName === `${formData.genus?.charAt(0)}. ${formData.species}`)) {
      autoShort = `${autoGenus.charAt(0)}. ${autoSpecies}`;
    }

    setFormData((prev) => ({
      ...prev,
      organismName: val,
      genus: autoGenus,
      species: autoSpecies,
      shortName: autoShort,
      displayName: autoShort ? `${val} (${autoShort})` : val
    }));

    if (validationErrors.organismName) {
      setValidationErrors((prev) => ({ ...prev, organismName: null }));
    }
  };

  const handleAddSynonymTag = (e) => {
    if (e) e.preventDefault();
    const clean = synonymInput.trim();
    if (!clean) return;

    if (synonymTags.some((t) => t.toLowerCase() === clean.toLowerCase())) {
      setValidationErrors((prev) => ({ ...prev, synonyms: 'Duplicate synonym already added' }));
      return;
    }

    const updated = [...synonymTags, clean];
    setSynonymTags(updated);
    setSynonymInput('');
    setFormData((prev) => ({ ...prev, synonyms: updated.join(', ') }));
    setValidationErrors((prev) => ({ ...prev, synonyms: null }));
  };

  const handleRemoveSynonymTag = (tagToRemove) => {
    const updated = synonymTags.filter((t) => t !== tagToRemove);
    setSynonymTags(updated);
    setFormData((prev) => ({ ...prev, synonyms: updated.join(', ') }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.organismCode.trim()) {
      errors.organismCode = 'Organism Code is required (e.g. E_COLI)';
    } else {
      // Check for code duplication in existing list
      const isDuplicate = organisms.some(
        (o) =>
          o.organismCode?.toUpperCase() === formData.organismCode.trim().toUpperCase() &&
          o.organismID !== formData.organismID
      );
      if (isDuplicate) {
        errors.organismCode = 'Organism Code already exists in master catalog';
      }
    }

    if (!formData.organismName.trim()) {
      errors.organismName = 'Binomial / Scientific Name is required (e.g. Escherichia coli)';
    }

    if (!formData.organismGroupID) {
      errors.organismGroupID = 'Taxonomic Group selection is required';
    }

    if (!formData.gramType) {
      errors.gramType = 'Gram Type is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (targetStatus) => {
    if (!validateForm()) {
      return;
    }

    try {
      const finalMorphology =
        formData.morphology === 'Other' && formData.customMorphology.trim()
          ? formData.customMorphology.trim()
          : formData.morphology;

      // Prepare remarks to preserve structured clinical & reference metadata
      let combinedRemarks = formData.remarks.trim();
      if (formData.clinicalNotes.trim()) {
        combinedRemarks += (combinedRemarks ? ' | ' : '') + `Clinical: ${formData.clinicalNotes.trim()}`;
      }

      const payload = {
        organismID: formData.organismID,
        organismCode: formData.organismCode.trim().toUpperCase(),
        organismName: formData.organismName.trim(),
        shortName: formData.shortName.trim() || null,
        synonyms: synonymTags.join(', ') || null,
        organismGroupID: parseInt(formData.organismGroupID, 10),
        organismFamily: formData.organismFamily.trim() || null,
        gramType: formData.gramType,
        morphology: finalMorphology || null,
        aerobicType: formData.aerobicType,
        clinicalCategory: formData.clinicalCategory,
        isPathogenic: formData.clinicalCategory === 'Primary Pathogen' || formData.clinicalCategory === 'Opportunistic Pathogen',
        displayOrder: parseInt(formData.displayOrder, 10) || 0,
        remarks: combinedRemarks || null,
        isActive: targetStatus === 'Active'
      };

      await microbiologyService.saveOrganism(payload);
      setIsModalOpen(false);
      loadData();
    } catch (err) {
      setValidationErrors((prev) => ({
        ...prev,
        submit: err.response?.data?.message || 'Failed to save microorganism. Please check server.'
      }));
    }
  };

  const getGramBadgeClass = (gram) => {
    if (gram === 'Gram Positive' || gram === 'Gram-Positive') return 'badge-gram-pos';
    if (gram === 'Gram Negative' || gram === 'Gram-Negative') return 'badge-gram-neg';
    if (gram === 'Yeast' || gram === 'Mold') return 'badge-yeast';
    return 'badge-nb';
  };

  return (
    <div className="micro-master-container">
      <div className="micro-header-section">
        <div className="micro-header-title">
          <h1>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
              <circle cx="12" cy="12" r="9" />
              <path d="M9 10h.01M15 10h.01M9.5 15a3.5 3.5 0 0 0 5 0" />
            </svg>
            Microbiology Organism Master
          </h1>
          <p>Canonical microorganism catalog, taxonomy, Gram classifications, and synonym aliases</p>
        </div>
        <div className="micro-header-actions">
          <button className="micro-btn-primary" onClick={handleOpenAdd}>
            + Add New Organism
          </button>
        </div>
      </div>

      <div className="micro-filter-bar">
        <div className="micro-search-input">
          <input
            type="text"
            placeholder="Search by organism name, code, short name or alias..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="micro-filter-select"
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
        >
          <option value="">All Taxonomic Groups</option>
          {groups.map((g) => (
            <option key={g.organismGroupID} value={g.organismGroupID}>
              {g.groupName}
            </option>
          ))}
        </select>

        <select
          className="micro-filter-select"
          value={selectedGram}
          onChange={(e) => setSelectedGram(e.target.value)}
        >
          <option value="">All Gram Types</option>
          <option value="Gram Negative">Gram Negative</option>
          <option value="Gram Positive">Gram Positive</option>
          <option value="Gram Variable">Gram Variable</option>
          <option value="Yeast">Yeast / Fungi</option>
          <option value="Other">Other / Anaerobes</option>
        </select>
      </div>

      <div className="micro-table-wrapper">
        <table className="micro-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Organism Name</th>
              <th>Short Name</th>
              <th>Taxonomic Group</th>
              <th>Gram Type</th>
              <th>Morphology</th>
              <th>Atmosphere</th>
              <th>Category</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '32px' }}>
                  Loading organism catalog...
                </td>
              </tr>
            ) : organisms.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '32px' }}>
                  No organisms matched your filter criteria.
                </td>
              </tr>
            ) : (
              pagination.pagedItems.map((org) => (
                <tr key={org.organismID}>
                  <td>
                    <strong>{org.organismCode}</strong>
                  </td>
                  <td>
                    <em>{org.organismName}</em>
                    {org.synonyms && (
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Aliases: {org.synonyms}
                      </div>
                    )}
                  </td>
                  <td>{org.shortName || '—'}</td>
                  <td>{org.groupName}</td>
                  <td>
                    <span className={`micro-badge ${getGramBadgeClass(org.gramType)}`}>
                      {org.gramType}
                    </span>
                  </td>
                  <td>{org.morphology || '—'}</td>
                  <td>{org.aerobicType}</td>
                  <td>
                    <span
                      className="micro-badge"
                      style={{
                        background: org.clinicalCategory === 'Primary Pathogen' || org.clinicalCategory === 'Pathogen' ? '#fee2e2' : '#f1f5f9',
                        color: org.clinicalCategory === 'Primary Pathogen' || org.clinicalCategory === 'Pathogen' ? '#b91c1c' : '#475569'
                      }}
                    >
                      {org.clinicalCategory}
                    </span>
                  </td>
                  <td>
                    <span
                      className="micro-badge"
                      style={{
                        background: org.isActive ? '#dcfce7' : '#f1f5f9',
                        color: org.isActive ? '#15803d' : '#64748b'
                      }}
                    >
                      {org.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="micro-btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                      onClick={() => handleOpenEdit(org)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Centralized Standard LIMS Pagination */}
      <CommonPagination
        {...pagination.paginationProps}
        loading={loading}
      />

      {/* Redesigned Add / Edit Microorganism Form Modal */}
      {isModalOpen && (
        <div className="micro-modal-overlay" role="dialog" aria-modal="true">
          <div className="micro-modal-content">
            {/* Header */}
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M9 10h.01M15 10h.01M9.5 15a3.5 3.5 0 0 0 5 0" />
                  </svg>
                  {editingOrg ? 'Edit Microorganism' : 'Add Microorganism'}
                </h3>
                <p>Define and maintain standardized microorganism master data</p>
              </div>
              <button
                className="micro-modal-close-btn"
                onClick={() => setIsModalOpen(false)}
                title="Close"
                aria-label="Close form"
              >
                &times;
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div className="micro-modal-body">
              {validationErrors.submit && (
                <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#991b1b', fontSize: '0.84rem' }}>
                  {validationErrors.submit}
                </div>
              )}

              {/* 1. Identity Card */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>1. Organism Identity</span>
                  <span className="section-badge">Required Identifiers</span>
                </div>
                <div className="micro-form-grid-2">
                  <div className="micro-form-group">
                    <label>
                      <span>Organism Code <span className="req-star">*</span></span>
                    </label>
                    <input
                      type="text"
                      className={validationErrors.organismCode ? 'is-invalid' : ''}
                      placeholder="e.g. E_COLI, STAPH_AUR"
                      value={formData.organismCode}
                      onChange={(e) => {
                        setFormData({ ...formData, organismCode: e.target.value.toUpperCase() });
                        if (validationErrors.organismCode) setValidationErrors({ ...validationErrors, organismCode: null });
                      }}
                    />
                    {validationErrors.organismCode ? (
                      <span className="micro-form-error">{validationErrors.organismCode}</span>
                    ) : (
                      <span className="micro-form-helper">Unique standardized code for lab identification</span>
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>
                      <span>Binomial / Scientific Name <span className="req-star">*</span></span>
                    </label>
                    <input
                      type="text"
                      className={validationErrors.organismName ? 'is-invalid' : ''}
                      placeholder="e.g. Escherichia coli"
                      value={formData.organismName}
                      onChange={(e) => handleScientificNameChange(e.target.value)}
                    />
                    {validationErrors.organismName ? (
                      <span className="micro-form-error">{validationErrors.organismName}</span>
                    ) : (
                      <span className="micro-form-helper">Full binomial nomenclature (Genus + species)</span>
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>Short Name / Abbreviation</label>
                    <input
                      type="text"
                      placeholder="e.g. E. coli"
                      value={formData.shortName}
                      onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
                    />
                    <span className="micro-form-helper">Standardized clinical abbreviation</span>
                  </div>

                  <div className="micro-form-group">
                    <label>Display Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Escherichia coli (E. coli)"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    />
                    <span className="micro-form-helper">Label displayed across laboratory screens and patient reports</span>
                  </div>
                </div>
              </div>

              {/* 2. Taxonomic Classification Card */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>2. Taxonomic Classification</span>
                  <span className="section-badge">Taxonomy Hierarchy</span>
                </div>
                <div className="micro-form-grid-3">
                  <div className="micro-form-group">
                    <label>
                      <span>Taxonomic Group <span className="req-star">*</span></span>
                    </label>
                    <select
                      className={validationErrors.organismGroupID ? 'is-invalid' : ''}
                      value={formData.organismGroupID}
                      onChange={(e) => {
                        setFormData({ ...formData, organismGroupID: e.target.value });
                        if (validationErrors.organismGroupID) setValidationErrors({ ...validationErrors, organismGroupID: null });
                      }}
                    >
                      <option value="">Select Group...</option>
                      {groups.map((g) => (
                        <option key={g.organismGroupID} value={g.organismGroupID}>
                          {g.groupName}
                        </option>
                      ))}
                    </select>
                    {validationErrors.organismGroupID && (
                      <span className="micro-form-error">{validationErrors.organismGroupID}</span>
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>Kingdom</label>
                    <select
                      value={formData.kingdom}
                      onChange={(e) => setFormData({ ...formData, kingdom: e.target.value })}
                    >
                      <option value="Bacteria">Bacteria</option>
                      <option value="Fungi">Fungi</option>
                      <option value="Archaea">Archaea</option>
                      <option value="Viruses">Viruses</option>
                      <option value="Parasites">Parasites</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Phylum</label>
                    <select
                      value={formData.phylum}
                      onChange={(e) => setFormData({ ...formData, phylum: e.target.value })}
                    >
                      <option value="Pseudomonadota">Pseudomonadota (Proteobacteria)</option>
                      <option value="Bacillota">Bacillota (Firmicutes)</option>
                      <option value="Actinomycetota">Actinomycetota (Actinobacteria)</option>
                      <option value="Bacteroidota">Bacteroidota</option>
                      <option value="Ascomycota">Ascomycota</option>
                      <option value="Basidiomycota">Basidiomycota</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Class</label>
                    <select
                      value={formData.taxClass}
                      onChange={(e) => setFormData({ ...formData, taxClass: e.target.value })}
                    >
                      <option value="Gammaproteobacteria">Gammaproteobacteria</option>
                      <option value="Alphaproteobacteria">Alphaproteobacteria</option>
                      <option value="Betaproteobacteria">Betaproteobacteria</option>
                      <option value="Bacilli">Bacilli</option>
                      <option value="Clostridia">Clostridia</option>
                      <option value="Actinomycetes">Actinomycetes</option>
                      <option value="Saccharomycetes">Saccharomycetes</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Order</label>
                    <select
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                    >
                      <option value="Enterobacterales">Enterobacterales</option>
                      <option value="Pseudomonadales">Pseudomonadales</option>
                      <option value="Bacillales">Bacillales</option>
                      <option value="Lactobacillales">Lactobacillales</option>
                      <option value="Burkholderiales">Burkholderiales</option>
                      <option value="Staphylococcales">Staphylococcales</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Family</label>
                    <input
                      type="text"
                      placeholder="e.g. Enterobacteriaceae"
                      value={formData.organismFamily}
                      onChange={(e) => setFormData({ ...formData, organismFamily: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Genus</label>
                    <input
                      type="text"
                      placeholder="e.g. Escherichia"
                      value={formData.genus}
                      onChange={(e) => setFormData({ ...formData, genus: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Species</label>
                    <input
                      type="text"
                      placeholder="e.g. coli"
                      value={formData.species}
                      onChange={(e) => setFormData({ ...formData, species: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* 3. Biological & Microscopic Characteristics Card */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>3. Organism Characteristics</span>
                  <span className="section-badge">Morphology & Atmosphere</span>
                </div>
                <div className="micro-form-grid-3">
                  <div className="micro-form-group">
                    <label>
                      <span>Gram Type <span className="req-star">*</span></span>
                    </label>
                    <select
                      className={validationErrors.gramType ? 'is-invalid' : ''}
                      value={formData.gramType}
                      onChange={(e) => setFormData({ ...formData, gramType: e.target.value })}
                    >
                      <option value="Gram Negative">Gram Negative</option>
                      <option value="Gram Positive">Gram Positive</option>
                      <option value="Gram Variable">Gram Variable</option>
                      <option value="Not Applicable">Not Applicable</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Microscopic Morphology</label>
                    <select
                      value={formData.morphology}
                      onChange={(e) => setFormData({ ...formData, morphology: e.target.value })}
                    >
                      <option value="Bacilli">Bacilli (Rods)</option>
                      <option value="Cocci">Cocci (Spheres)</option>
                      <option value="Coccobacilli">Coccobacilli</option>
                      <option value="Curved Rods">Curved Rods</option>
                      <option value="Spiral">Spiral</option>
                      <option value="Filamentous">Filamentous</option>
                      <option value="Yeast">Yeast</option>
                      <option value="Hyphal">Hyphal</option>
                      <option value="Other">Other</option>
                    </select>
                    {formData.morphology === 'Other' && (
                      <input
                        type="text"
                        style={{ marginTop: '6px' }}
                        placeholder="Specify custom morphology..."
                        value={formData.customMorphology}
                        onChange={(e) => setFormData({ ...formData, customMorphology: e.target.value })}
                      />
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>Atmosphere Requirement</label>
                    <select
                      value={formData.aerobicType}
                      onChange={(e) => setFormData({ ...formData, aerobicType: e.target.value })}
                    >
                      <option value="Facultative Anaerobe">Facultative Anaerobe</option>
                      <option value="Strict Aerobe">Strict Aerobe</option>
                      <option value="Strict Anaerobe">Strict Anaerobe</option>
                      <option value="Microaerophilic">Microaerophilic</option>
                      <option value="Aerotolerant">Aerotolerant</option>
                      <option value="Capnophilic">Capnophilic</option>
                      <option value="Not Applicable">Not Applicable</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 4. Biological Attributes Card */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>4. Biological Attributes</span>
                  <span className="section-badge">Physiological Properties</span>
                </div>
                <div className="micro-form-grid-5">
                  <div className="micro-form-group">
                    <label>Cell Arrangement</label>
                    <select
                      value={formData.cellArrangement}
                      onChange={(e) => setFormData({ ...formData, cellArrangement: e.target.value })}
                    >
                      <option value="Single">Single</option>
                      <option value="Pairs">Pairs (Diplococci)</option>
                      <option value="Chains">Chains</option>
                      <option value="Clusters">Clusters</option>
                      <option value="Tetrads">Tetrads</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Motility</label>
                    <select
                      value={formData.motility}
                      onChange={(e) => setFormData({ ...formData, motility: e.target.value })}
                    >
                      <option value="Motile">Motile</option>
                      <option value="Non-Motile">Non-Motile</option>
                      <option value="Variable">Variable</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Spore Formation</label>
                    <select
                      value={formData.sporeFormation}
                      onChange={(e) => setFormData({ ...formData, sporeFormation: e.target.value })}
                    >
                      <option value="Non-spore-forming">Non-spore-forming</option>
                      <option value="Spore-forming">Spore-forming</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Capsule</label>
                    <select
                      value={formData.capsule}
                      onChange={(e) => setFormData({ ...formData, capsule: e.target.value })}
                    >
                      <option value="Absent">Absent</option>
                      <option value="Present">Present</option>
                      <option value="Variable">Variable</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                  </div>

                  <div className="micro-form-group">
                    <label>Acid-Fast</label>
                    <select
                      value={formData.acidFast}
                      onChange={(e) => setFormData({ ...formData, acidFast: e.target.value })}
                    >
                      <option value="Negative">Negative</option>
                      <option value="Positive">Positive</option>
                      <option value="Not Applicable">Not Applicable</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 5. Clinical Classification Card */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>5. Clinical Classification</span>
                  <span className="section-badge">Significance & Pathogenesis</span>
                </div>
                <div className="micro-form-grid-2">
                  <div className="micro-form-group">
                    <label>Clinical Category</label>
                    <select
                      value={formData.clinicalCategory}
                      onChange={(e) => setFormData({ ...formData, clinicalCategory: e.target.value })}
                    >
                      <option value="Primary Pathogen">Primary Pathogen</option>
                      <option value="Opportunistic Pathogen">Opportunistic Pathogen</option>
                      <option value="Commensal">Commensal</option>
                      <option value="Normal Flora">Normal Flora</option>
                      <option value="Colonizer">Colonizer</option>
                      <option value="Contaminant">Contaminant</option>
                      <option value="Clinically Significant — Context Dependent">Clinically Significant — Context Dependent</option>
                      <option value="Non-pathogenic">Non-pathogenic</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                    <span className="micro-form-helper">Inherent pathogenicity profile in clinical specimens</span>
                  </div>

                  <div className="micro-form-group">
                    <label>Clinical Significance Notes</label>
                    <textarea
                      rows="2"
                      placeholder="Brief context notes (e.g. Causative agent of UTI, bacteremia, and sepsis)..."
                      value={formData.clinicalNotes}
                      onChange={(e) => setFormData({ ...formData, clinicalNotes: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* 6. Synonyms & Laboratory Notes Card */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>6. Synonyms & Laboratory Notes</span>
                  <span className="section-badge">Aliases & Chip Tags</span>
                </div>
                <div className="micro-form-grid-2">
                  <div className="micro-form-group">
                    <label>
                      <span>Synonyms / Aliases</span>
                      <span style={{ fontSize: '0.74rem', color: '#64748b' }}>Press Enter or Comma to add tag</span>
                    </label>
                    <div className="micro-tag-input-box">
                      {synonymTags.map((tag) => (
                        <span key={tag} className="micro-tag-chip">
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveSynonymTag(tag)}
                            title="Remove synonym"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        className="micro-tag-input-field"
                        placeholder={synonymTags.length === 0 ? "Type synonym & press Enter..." : "Add another..."}
                        value={synonymInput}
                        onChange={(e) => setSynonymInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            handleAddSynonymTag();
                          }
                        }}
                      />
                    </div>
                    {validationErrors.synonyms && (
                      <span className="micro-form-error">{validationErrors.synonyms}</span>
                    )}
                  </div>

                  <div className="micro-form-group">
                    <label>Remarks / Notes</label>
                    <textarea
                      rows="2"
                      placeholder="Add relevant laboratory or classification notes..."
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* 7. Lifecycle Status & Review Metadata Card */}
              <div className="micro-section-card">
                <div className="micro-section-title">
                  <span>7. Status & Reference Metadata</span>
                  <span className="section-badge">Lifecycle & Audit</span>
                </div>
                <div className="micro-form-grid-4">
                  <div className="micro-form-group">
                    <label>Status <span className="req-star">*</span></label>
                    <div className="micro-status-group">
                      {['Draft', 'Active', 'Inactive', 'Deprecated'].map((st) => (
                        <button
                          key={st}
                          type="button"
                          className={`micro-status-btn ${formData.status === st ? `active-status-${st.toLowerCase()}` : ''}`}
                          onClick={() => setFormData({ ...formData, status: st })}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="micro-form-group">
                    <label>Reference Source</label>
                    <input
                      type="text"
                      placeholder="e.g. CLSI M100 / NCBI"
                      value={formData.referenceSource}
                      onChange={(e) => setFormData({ ...formData, referenceSource: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Reference Version</label>
                    <input
                      type="text"
                      placeholder="e.g. 2026.1 / M100-ED34"
                      value={formData.referenceVersion}
                      onChange={(e) => setFormData({ ...formData, referenceVersion: e.target.value })}
                    />
                  </div>

                  <div className="micro-form-group">
                    <label>Last Reviewed Date</label>
                    <input
                      type="date"
                      value={formData.lastReviewedDate}
                      onChange={(e) => setFormData({ ...formData, lastReviewedDate: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer with Actions */}
            <div className="micro-modal-footer">
              <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                <span style={{ color: '#e11d48', fontWeight: 600 }}>*</span> Mandatory fields for laboratory standardization
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="micro-btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="micro-btn-secondary"
                  style={{ background: '#f8fafc', borderColor: '#cbd5e1', color: '#334155' }}
                  onClick={() => handleSave('Draft')}
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  className="micro-btn-success"
                  onClick={() => handleSave('Active')}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Save &amp; Activate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

